import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import ChatUserPicker from "@/components/ChatUserPicker";
import { displayAuthorName } from "@/lib/anonAuthor";
import { MessageCircle, UserPlus, Ban, Check, X, Loader2, Users, ShieldAlert } from "lucide-react";

/**
 * Phase 3 messaging hub: lists all 1:1 chats + groups + pending
 * requests + pending group invites + blocked users. 18+ only —
 * anyone underage is blocked at the API level (403) and we surface
 * the localised banner instead of the tabs.
 */
export default function ChatsPage() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "chats");
  const [chats, setChats] = useState([]);
  const [reqIn, setReqIn] = useState([]);
  const [reqOut, setReqOut] = useState([]);
  const [invites, setInvites] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isMinor = useMemo(() => {
    if (!user?.date_of_birth) return false;
    try {
      const [y, m, d] = user.date_of_birth.split("-").map(Number);
      const today = new Date();
      const age = today.getFullYear() - y - ((today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) ? 1 : 0);
      return age < 18;
    } catch { return false; }
  }, [user]);

  const load = async () => {
    if (!user || isMinor) return;
    try {
      const [c, ri, ro, inv, bl] = await Promise.all([
        api.get("/chat/chats"),
        api.get("/chat/requests", { params: { direction: "incoming", status: "pending" } }),
        api.get("/chat/requests", { params: { direction: "outgoing", status: "pending" } }),
        api.get("/chat/group-invites", { params: { status: "pending" } }),
        api.get("/chat/blocks"),
      ]);
      setChats(c.data.items || []);
      setReqIn(ri.data.items || []);
      setReqOut(ro.data.items || []);
      setInvites(inv.data.items || []);
      setBlocks(bl.data.items || []);
    } catch (e) {
      // Silent — the banner covers unauthenticated / 403 states.
    }
  };

  useEffect(() => {
    if (!loading && !user) { nav("/auth?mode=signin"); return; }
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  // eslint-disable-next-line
  }, [user, isMinor, loading]);

  useEffect(() => { setSp({ tab }, { replace: true }); }, [tab]); // eslint-disable-line

  const acceptReq = async (rid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/requests/${rid}/accept`);
      nav(`/chats/${data.chat_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    } finally { setBusy(false); }
  };
  const rejectReq = async (rid) => {
    setBusy(true);
    try {
      await api.post(`/chat/requests/${rid}/reject`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    } finally { setBusy(false); }
  };
  const cancelReq = async (rid) => {
    setBusy(true);
    try {
      await api.post(`/chat/requests/${rid}/cancel`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    } finally { setBusy(false); }
  };
  const acceptInvite = async (iid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/group-invites/${iid}/accept`);
      nav(`/chats/${data.chat_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    } finally { setBusy(false); }
  };
  const rejectInvite = async (iid) => {
    setBusy(true);
    try { await api.post(`/chat/group-invites/${iid}/reject`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("chat_error_send")); }
    finally { setBusy(false); }
  };
  const unblock = async (uid) => {
    try { await api.delete(`/chat/blocks/${uid}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("chat_error_send")); }
  };

  if (loading) return null;
  if (!user) return null;
  if (isMinor) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <div className="max-w-2xl mx-auto p-4 pt-8">
          <BackButton />
          <div
            data-testid="chats-adult-only"
            className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 p-5"
          >
            <div className="flex items-center gap-2 font-semibold">
              <ShieldAlert size={18} />
              {t("chat_adult_only")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="max-w-3xl mx-auto p-4">
        <BackButton />
        <div className="flex items-center justify-between mt-2 mb-3 gap-2">
          <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="chats-title">
            {t("chat_title")}
          </h1>
          <Button
            data-testid="chats-create-group-btn"
            onClick={() => setCreateGroupOpen(true)}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
          >
            <UserPlus size={13} className="mr-1" /> {t("chat_group_create")}
          </Button>
        </div>

        <div className="flex gap-2 overflow-x-auto mb-3" data-testid="chats-tabs">
          {[
            ["chats", t("chat_tab_chats"), chats.length],
            ["requests", t("chat_tab_requests"), reqIn.length + reqOut.length],
            ["invites", t("chat_tab_invites"), invites.length],
            ["blocked", t("chat_tab_blocked"), blocks.length],
          ].map(([k, label, n]) => (
            <button
              key={k}
              data-testid={`chats-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-3 h-9 rounded-full whitespace-nowrap text-sm ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"}`}
            >
              {label}{n > 0 && <span className="ml-1 text-xs opacity-70">({n})</span>}
            </button>
          ))}
        </div>

        {tab === "chats" && (
          <div className="space-y-2" data-testid="chats-list">
            {chats.length === 0 && (
              <div className="text-sm text-slate-500 italic py-8 text-center" data-testid="chats-empty">
                {t("chat_empty")}
              </div>
            )}
            {chats.map((c) => (
              <Link
                key={c.chat_id}
                to={`/chats/${c.chat_id}`}
                data-testid={`chat-row-${c.chat_id}`}
                className="block bg-white border border-slate-200 rounded-2xl p-3 hover:border-emerald-300 hover:bg-emerald-50/40 transition"
              >
                <div className="flex items-center gap-3">
                  {c.kind === "group" ? (
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                      <Users size={18} />
                    </div>
                  ) : c.peer?.picture ? (
                    <img src={c.peer.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                      <MessageCircle size={18} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">
                      {c.kind === "group" ? c.title : c.peer?.name || "—"}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.last_message?.text || (c.my_role === "pending" ? t("chat_pending_invite_banner") : t("chat_empty"))}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {c.unread > 0 && (
                      <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                        {c.unread > 99 ? "99+" : c.unread}
                      </span>
                    )}
                    {c.origin_type === "marketplace_listing" && (
                      <Badge variant="outline" className="text-[9px]">Marketplace</Badge>
                    )}
                    {c.my_role === "pending" && (
                      <Badge variant="outline" className="text-[9px]">{t("chat_pending_status")}</Badge>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div className="space-y-3" data-testid="chats-requests">
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-widest mb-1">{t("chat_incoming")}</div>
              {reqIn.length === 0 && <div className="text-xs text-slate-400 italic">{t("chat_no_requests")}</div>}
              {reqIn.map((r) => (
                <div key={r.request_id} className="bg-white border border-slate-200 rounded-2xl p-3 mb-2" data-testid={`chat-req-in-${r.request_id}`}>
                  <div className="flex items-center gap-3">
                    {r.from_picture ? (
                      <img src={r.from_picture} className="w-10 h-10 rounded-full object-cover" alt="" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-200" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{displayAuthorName({ author_name: r.from_name, user_deleted: r.user_deleted }, t)}</div>
                      {r.initial_message && (
                        <div className="text-xs text-slate-600 mt-1 line-clamp-3">{r.initial_message}</div>
                      )}
                      {r.origin_type === "marketplace_listing" && (
                        <Badge variant="outline" className="text-[9px] mt-1">Marketplace</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      data-testid={`chat-req-accept-${r.request_id}`}
                      disabled={busy}
                      size="sm"
                      onClick={() => acceptReq(r.request_id)}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
                    >
                      <Check size={13} className="mr-1" /> {t("chat_accept")}
                    </Button>
                    <Button
                      data-testid={`chat-req-reject-${r.request_id}`}
                      disabled={busy}
                      size="sm"
                      variant="outline"
                      onClick={() => rejectReq(r.request_id)}
                      className="rounded-full"
                    >
                      <X size={13} className="mr-1" /> {t("chat_reject")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-widest mb-1">{t("chat_outgoing")}</div>
              {reqOut.length === 0 && <div className="text-xs text-slate-400 italic">{t("chat_no_requests")}</div>}
              {reqOut.map((r) => (
                <div key={r.request_id} className="bg-white border border-slate-200 rounded-2xl p-3 mb-2" data-testid={`chat-req-out-${r.request_id}`}>
                  <div className="flex items-center gap-3">
                    {r.to_picture ? (
                      <img src={r.to_picture} className="w-10 h-10 rounded-full object-cover" alt="" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-200" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{displayAuthorName({ author_name: r.to_name, user_deleted: r.user_deleted }, t)}</div>
                      <Badge variant="outline" className="text-[9px] mt-1">{t("chat_pending_status")}</Badge>
                    </div>
                    <Button
                      data-testid={`chat-req-cancel-${r.request_id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => cancelReq(r.request_id)}
                      disabled={busy}
                      className="rounded-full"
                    >
                      {t("chat_cancel_req")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "invites" && (
          <div className="space-y-2" data-testid="chats-invites">
            {invites.length === 0 && <div className="text-sm text-slate-400 italic py-8 text-center">{t("chat_no_invites")}</div>}
            {invites.map((inv) => (
              <div key={inv.invite_id} className="bg-white border border-slate-200 rounded-2xl p-3" data-testid={`chat-invite-${inv.invite_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                    <Users size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900">{inv.group_title}</div>
                    <div className="text-xs text-slate-500">{t("chat_from")} {displayAuthorName({ author_name: inv.from_name, user_deleted: inv.user_deleted }, t)}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button
                    data-testid={`chat-invite-accept-${inv.invite_id}`}
                    size="sm"
                    onClick={() => acceptInvite(inv.invite_id)}
                    disabled={busy}
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
                  >
                    <Check size={13} className="mr-1" /> {t("chat_accept")}
                  </Button>
                  <Button
                    data-testid={`chat-invite-reject-${inv.invite_id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => rejectInvite(inv.invite_id)}
                    disabled={busy}
                    className="rounded-full"
                  >
                    <X size={13} className="mr-1" /> {t("chat_reject")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "blocked" && (
          <div className="space-y-2" data-testid="chats-blocked">
            {blocks.length === 0 && <div className="text-sm text-slate-400 italic py-8 text-center">{t("chat_blocked_list")}</div>}
            {blocks.map((b) => (
              <div key={b.blocked_id} className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center gap-3" data-testid={`chat-block-${b.blocked_id}`}>
                <Ban size={18} className="text-red-500" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{b.blocked?.name || b.blocked_id}</div>
                </div>
                <Button
                  data-testid={`chat-unblock-${b.blocked_id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => unblock(b.blocked_id)}
                  className="rounded-full"
                >
                  {t("chat_unblock_user")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateGroupDialog open={createGroupOpen} onOpenChange={setCreateGroupOpen} onCreated={load} />
    </div>
  );
}

function CreateGroupDialog({ open, onOpenChange, onCreated }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [invitees, setInvitees] = useState([]); // [{user_id,name,picture,role}]
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const invList = invitees.map((u) => u.user_id);
      const { data } = await api.post("/chat/groups", {
        title: title.trim(),
        description: desc.trim() || null,
        invitees: invList,
      });
      toast.success(t("chat_group_created"));
      onOpenChange(false);
      setTitle(""); setDesc(""); setInvitees([]);
      onCreated && onCreated();
      nav(`/chats/${data.chat_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="chat-create-group-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("chat_group_create")}</DialogTitle>
          <DialogDescription>{t("chat_adult_only")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">{t("chat_group_title_label")}</label>
            <Input
              data-testid="chat-group-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">{t("chat_group_desc_label")}</label>
            <Textarea
              data-testid="chat-group-desc-input"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              maxLength={1000}
              className="min-h-[60px]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">{t("chat_group_picker_label")}</label>
            <ChatUserPicker
              value={invitees}
              onChange={setInvitees}
              testidPrefix="chat-group-picker"
              max={49}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="chat-group-create-cancel">
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            data-testid="chat-group-create-submit"
            onClick={submit}
            disabled={busy || !title.trim()}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
          >
            {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <UserPlus size={13} className="mr-1" />}
            {t("chat_group_create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
